/** @format */

import React, { useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import AIChatButton from '../components/AIChatButton';
import { Link } from "react-router-dom";
import {
  BookOpenIcon,
  RocketLaunchIcon,
  SparklesIcon,
  AcademicCapIcon,
  TrophyIcon,
  PlayIcon
} from "@heroicons/react/24/outline";

const Mentorship: React.FC = () => {
  const { currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const isStudent = currentUser?.role === "student";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    if (!isStudent) {
      toast.error("Only students can request to become mentors");
      return;
    }

    try {
      setSubmitting(true);

      const requestId = `MENTORREQ_${currentUser.uid}_${Date.now()}`;
      const payload = {
        id: requestId,
        userId: currentUser.uid,
        email: currentUser.email,
        name: currentUser.name || "",
        role: currentUser.role,
        status: "pending" as const,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(doc(db, "mentorRequests", requestId), JSON.parse(JSON.stringify(payload)));
      toast.success("Mentorship request submitted. Admin will review.");
    } catch (error) {
      console.error("Failed to submit mentorship request", error);
      toast.error("Failed to submit mentorship request");
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <AcademicCapIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Mentorship Program
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Become a</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Mentor
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Share your knowledge, guide fellow students, and contribute to the learning community. Apply to become a mentor and help others succeed.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    Student
                  </div>
                  <div className="text-xs text-blue-200">Your Role</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {isStudent ? "Eligible" : "Not Eligible"}
                  </div>
                  <div className="text-xs text-blue-200">Status</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    Pending
                  </div>
                  <div className="text-xs text-blue-200">Review</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={handleSubmit}
                  disabled={!isStudent || submitting}
                  className={`inline-flex items-center justify-center px-3 sm:px-4 py-2 font-semibold rounded-lg transition-all duration-200 shadow-lg text-sm ${
                    !isStudent || submitting 
                      ? "bg-gray-400 cursor-not-allowed text-gray-600" 
                      : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
                  }`}
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
                  {submitting ? "Submitting..." : "Apply Now"}
                </button>
                <Link
                  to="/app/courses"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <SparklesIcon className="h-4 w-4 mr-1.5" />
                  View Courses
                </Link>
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Mentorship Cards */}
              <div className="space-y-4">
                {/* Student Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <AcademicCapIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Student Status</h3>
                      <p className="text-blue-200 text-sm">Currently enrolled as a student</p>
                    </div>
                  </div>
                </div>

                {/* Mentorship Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Mentorship</h3>
                      <p className="text-blue-200 text-sm">Help others succeed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 relative z-20">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Mentorship Application</h2>
          <p className="text-gray-600 mb-6">Request to become a mentor. Only students are eligible to apply.</p>

          {!isStudent && (
            <div className="mb-6 p-4 rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-800">
              Only students can request to become mentors. Your current role is "{currentUser.role}".
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={currentUser.email}
                disabled
                className="w-full rounded-md border-gray-300 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={currentUser.name || ""}
                disabled
                className="w-full rounded-md border-gray-300 bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input
                  type="text"
                  value={currentUser.role}
                  disabled
                  className="w-full rounded-md border-gray-300 bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <input
                  type="text"
                  value={currentUser.uid}
                  disabled
                  className="w-full rounded-md border-gray-300 bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!isStudent || submitting}
              className={`inline-flex items-center justify-center px-4 py-2 rounded-md text-white ${
                !isStudent || submitting ? "bg-gray-400 cursor-not-allowed" : "bg-primary-600 hover:bg-primary-700"
              }`}
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        </div>

        {/* AI Chat Button */}
        <div className="mt-6">
          <AIChatButton />
        </div>
      </div>
    </div>
  );
};

export default Mentorship;


